import { DSVRowString } from 'd3';
import { map, Observable } from 'rxjs';
import { FilterValuesWithData } from '../../../../data_controller/dashboard_data_controller';
import { kEurShort } from 'src/app/shared/utils/k-eur-short';
import { toNumDE } from 'src/app/shared/utils/to-num-de';
import { toPercentString } from 'src/app/shared/utils/to-percent-string';
import { selectedKpi } from 'src/app/dashboards/shared/dashboard-config/kpis/kpi-options';
import {
  DashboardDossierStatesInfoFieldConfig,
  DashboardDossierStatesInfoResolveParams,
  DashboardDossierStatesInfoTextResolver,
  DashboardDossierStatesInfoTopMarket,
  DashboardDossierStatesInfoValue,
  DashboardDossierStatesInfoValueFormat,
  DashboardDossierStatesInfoValueResolver,
  DashboardDossierStatesInfoViewConfig,
  DashboardDossierStatesInfoViewContext,
  DashboardDossierStatesInfoViewModel,
} from './dashboard-dossier-states-info.model';

export type CompanyType = 'companies' | 'small_companies';

export const companyTypeLabels: Record<CompanyType, string> = {
  companies: 'Unternehmen',
  small_companies: 'Kleinunternehmen',
};

export type EconomicParticipantType = 'total' | 'core_market' | 'marginal';

export const economicParticipantTypeLabels: Record<EconomicParticipantType, string> = {
  total: 'Erwerbstätige',
  core_market: 'Kernerwerbstätige',
  marginal: 'Geringfügig Erwerbstätige',
};

export type EmployedType = 'total' | 'social_security' | 'marginal';

export const employedTypeLabels: Record<EmployedType, string> = {
  total: 'Gesamtbeschäftigte',
  social_security: 'Sv-pflichtig Beschäftigte',
  marginal: 'Geringfügig Beschäftigte',
};

export type GrossValueAddedType = 'gross_value_added' | 'production_value';

export const grossValueAddedTypeLabels: Record<GrossValueAddedType, string> = {
  gross_value_added: 'Bruttowertschöpfung',
  production_value: 'Produktionswert',
};

/**
 * Legacy type-filter value of the selected KPI (e.g. 'social_security'), so the
 * type-keyed texts and column names below keep working with the KPI filter.
 */
function legacyTypeValue(context: DashboardDossierStatesInfoViewContext): string | undefined {
  return selectedKpi(context.filterValues)?.legacy?.value;
}

export function companyTypeLabel(context: DashboardDossierStatesInfoViewContext): string {
  const companyType = (legacyTypeValue(context) ?? 'companies') as CompanyType;
  return companyTypeLabels[companyType];
}

export function economicParticipantType(
  context: DashboardDossierStatesInfoViewContext,
): EconomicParticipantType {
  return (legacyTypeValue(context) ?? 'total') as EconomicParticipantType;
}

export function economicParticipantTypeLabel(
  context: DashboardDossierStatesInfoViewContext,
): string {
  return economicParticipantTypeLabels[economicParticipantType(context)];
}

export function employedType(context: DashboardDossierStatesInfoViewContext): EmployedType {
  return (legacyTypeValue(context) ?? 'total') as EmployedType;
}

export function employedTypeLabel(context: DashboardDossierStatesInfoViewContext): string {
  return employedTypeLabels[employedType(context)];
}

export function grossValueAddedType(
  context: DashboardDossierStatesInfoViewContext,
): GrossValueAddedType {
  return (legacyTypeValue(context) ?? 'gross_value_added') as GrossValueAddedType;
}

export function grossValueAddedTypeLabel(
  context: DashboardDossierStatesInfoViewContext,
): string {
  return grossValueAddedTypeLabels[grossValueAddedType(context)];
}

export function rowValue(row: DSVRowString, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '';
}

function resolveText(
  resolver: DashboardDossierStatesInfoTextResolver,
  params: DashboardDossierStatesInfoResolveParams,
): string {
  return typeof resolver === 'function' ? resolver(params) : resolver;
}

function resolveRawValue(
  resolver: DashboardDossierStatesInfoValueResolver,
  params: DashboardDossierStatesInfoResolveParams,
): string {
  if (typeof resolver === 'function') {
    return resolver(params);
  }
  if (typeof resolver !== 'string') {
    return rowValue(params.row, ...resolver);
  }
  return params.row[resolver] ?? '';
}

function formatValue(value: string, format: DashboardDossierStatesInfoValueFormat = 'raw'): string {
  switch (format) {
    case 'number':
      return toNumDE(value);
    case 'currency':
      return kEurShort(value);
    case 'percent':
      return toPercentString(value);
    case 'raw':
    default:
      return value;
  }
}

function toFieldValue(
  field: DashboardDossierStatesInfoFieldConfig,
  params: DashboardDossierStatesInfoResolveParams,
): DashboardDossierStatesInfoValue {
  return {
    label: resolveText(field.label, params),
    value: formatValue(resolveRawValue(field.valueFrom, params), field.format),
    emphasis: field.emphasis,
  };
}

function resolveFields(
  fields:
    | DashboardDossierStatesInfoFieldConfig[]
    | ((params: DashboardDossierStatesInfoResolveParams) => DashboardDossierStatesInfoFieldConfig[])
    | undefined,
  params: DashboardDossierStatesInfoResolveParams,
): DashboardDossierStatesInfoValue[] {
  if (!fields) {
    return [];
  }
  const configs = typeof fields === 'function' ? fields(params) : fields;
  return configs.map((field) => toFieldValue(field, params));
}

function selectInfoRow(
  rows: readonly DSVRowString[],
  context: DashboardDossierStatesInfoViewContext,
): DSVRowString | undefined {
  return rows.find((row) => context.sumSubMarketIds.includes(row['sub_market_id']));
}

function toTopMarket(
  config: NonNullable<DashboardDossierStatesInfoViewConfig['topMarket']>,
  params: DashboardDossierStatesInfoResolveParams,
): DashboardDossierStatesInfoTopMarket | undefined {
  const id = Number(resolveRawValue(config.idFrom ?? 'top_sub_market_id', params));
  const name = Number.isFinite(id) ? params.context.subMarketNames.get(id) : undefined;
  if (!name) {
    return undefined;
  }
  return {
    id,
    name,
    description: resolveText(config.description, params),
    value: formatValue(resolveRawValue(config.valueFrom, params), config.format),
  };
}

export function toDashboardDossierStatesInfoViewModel(
  view: DashboardDossierStatesInfoViewConfig,
  row: DSVRowString,
  context: DashboardDossierStatesInfoViewContext,
): DashboardDossierStatesInfoViewModel {
  const params: DashboardDossierStatesInfoResolveParams = { row, context };
  return {
    year: row['year'] ?? '',
    state: {
      id: Number(row['state_id']),
      name: row['state_name'] ?? '',
      code: row['state_code'] ?? '',
    },
    countryName: 'Deutschland',
    industryName: row['sum_sub_market_name'] ?? '',
    metric: view.metric ? toFieldValue(view.metric, params) : undefined,
    primary: toFieldValue(view.primary, params),
    details: resolveFields(view.details, params),
    showDetailArrows: view.showDetailArrows,
    shares: resolveFields(view.shares, params),
    topMarket: view.topMarket ? toTopMarket(view.topMarket, params) : undefined,
  };
}

type StaticViewContext = Omit<DashboardDossierStatesInfoViewContext, 'filterValues'>;

export function toDashboardDossierStatesInfoViewModel$(
  source$: Observable<FilterValuesWithData | null>,
  view: DashboardDossierStatesInfoViewConfig,
  staticContext: StaticViewContext,
): Observable<DashboardDossierStatesInfoViewModel | null> {
  return source$.pipe(
    map((result) => {
      if (!result?.data?.length) {
        return null;
      }

      const context: DashboardDossierStatesInfoViewContext = {
        ...staticContext,
        filterValues: result.filterValues,
      };

      const row = selectInfoRow(
        result.data as unknown as readonly DSVRowString[],
        context,
      );

      return row ? toDashboardDossierStatesInfoViewModel(view, row, context) : null;
    }),
  );
}
